import { useState, useEffect, useRef } from 'react';
import { settingsSelector, userSelector } from 'selectors';
import { useAppSelector, useAppDispatch } from 'storeHooks';
import { isAuthenticated } from 'actions/user';
import { showMaintenanceBanner } from 'actions/settings';
import { ONE_HOUR, TWENTY_FOUR_HOURS } from 'constants/constants';
import {
  convertTimeToMilliseconds,
  unixTimeDiffInSeconds,
  getMomentTimeFormat,
  compareDates,
} from 'helpers/utils';
import { dateFormat, getDateTime, toLocal, toUTC } from 'helpers/day';
import { TIME_FORMATS } from 'constants';

const useMaintenance = () => {
  const settings = useAppSelector(settingsSelector);
  const user = useAppSelector(userSelector);
  const isLoggedIn = isAuthenticated(user);
  const dispatch = useAppDispatch();

  const [showBanner, setShowBanner] = useState(false);
  const [isVisibleToUsers, setVisibleToUsers] = useState(false);
  const {
    maintenance_start_time,
    maintenance_finish_time,
    maintenance_announcement_time,
  } = settings;

  const timerRef: { current: NodeJS.Timeout | null } = useRef(null);
  const countdownRef: { current: number | null } = useRef(null);

  const maintenanceStartTimeInMilliseconds = convertTimeToMilliseconds(
    Number(maintenance_start_time)
  );
  const timeLeftToStartMaintenance = unixTimeDiffInSeconds(
    maintenanceStartTimeInMilliseconds
  );

  const [countdownTimer, setCountdownTimer] = useState(
    timeLeftToStartMaintenance
  );
  countdownRef.current = countdownTimer;

  const maintenanceFinishTimeInMilliseconds = convertTimeToMilliseconds(
    Number(maintenance_finish_time)
  );
  const maintenanceStartTime = dateFormat(
    toLocal(toUTC(getDateTime(maintenanceStartTimeInMilliseconds))),
    `${TIME_FORMATS.DD_MMM} ${TIME_FORMATS.LT}`
  );

  const maintenanceFinishTime = dateFormat(
    toLocal(toUTC(getDateTime(maintenanceFinishTimeInMilliseconds))),
    `${TIME_FORMATS.DD_MMM} ${TIME_FORMATS.LT}`
  );

  const isDatesEqual = compareDates(
    maintenanceStartTimeInMilliseconds,
    maintenanceFinishTimeInMilliseconds
  );

  const maintenanceDuration = unixTimeDiffInSeconds(
    maintenanceFinishTimeInMilliseconds,
    maintenanceStartTimeInMilliseconds
  );

  useEffect(() => {
    setCountdownTimer(timeLeftToStartMaintenance);
  }, [timeLeftToStartMaintenance]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (countdownRef.current <= 0) {
        clearInterval(timerRef.current);
        setShowBanner(false);
      } else {
        setCountdownTimer(state => state - 1);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [maintenance_start_time]);

  useEffect(() => {
    if (
      !isLoggedIn &&
      maintenanceDuration > 4 * ONE_HOUR &&
      countdownRef.current > 0 &&
      countdownRef.current <= TWENTY_FOUR_HOURS
    ) {
      setVisibleToUsers(true);
    }
  }, [isLoggedIn, countdownTimer]);

  useEffect(() => {
    dispatch(showMaintenanceBanner(showBanner));
  }, [showBanner]);

  return {
    isLoggedIn,
    showBanner,
    setShowBanner,
    maintenanceStartTime,
    maintenanceFinishTime,
    isDatesEqual,
    maintenanceDuration,
    timeLeftToStartMaintenance,
    maintenance_announcement_time,
    maintenanceFinishTimeInMilliseconds,
    isVisibleToUsers,
    maintenance_start_time,
    maintenance_finish_time,
    countdownTimer,
    countdownRef,
  };
};

export default useMaintenance;
